import { Carousel } from '../../components/Carousel';
import { ItemCard } from '../../components/ItemCard';
import type { ItemCardProps } from '../../components/ItemCard/ItemCard.types';
import type { ImageSizing } from '../../components/EntityImage/imageSizing';
import { PlusIcon } from '../../icons/PlusIcon';
import { useEffect, useRef } from 'react';
import { useDragToScroll } from '../../hooks/useDragToScroll';
import { useInView } from '../../hooks/useInView';
import { selectOnCardClick } from '../../utils/cardClick';
import {
  CenteredGrid,
  FullWidthCell,
  GridHeading,
  GridHeadingSecondary,
  GridStack,
  RecommendationsTitle,
  RecommendationsWrapper,
  RowHeading,
  RowSection,
  Tile,
  TileAction,
  TileImage,
  TileImg,
  TileLabel,
  TilePlaceholder,
  TileRow,
} from './Recommendations.styles';
import type { RecommendationItem, RecommendationsProps } from './Recommendations.types';

const TILE_IMAGE_SIZING: ImageSizing = { width: 320, aspectRatio: '1:1' };

function renderHeading(
  title: RecommendationsProps['title'],
  titleSecondary: RecommendationsProps['titleSecondary'],
  size: RecommendationsProps['titleSize']
) {
  if (title == null) return null;
  if (typeof title !== 'string') return title;
  if (titleSecondary == null) {
    return <GridHeading $size={size}>{title}</GridHeading>;
  }
  return (
    <GridHeading $size={size}>
      {title} <GridHeadingSecondary>{titleSecondary}</GridHeadingSecondary>
    </GridHeading>
  );
}

function renderItemCard(
  item: RecommendationItem,
  {
    fromPriceLabel,
    noImageLabel,
    onSelect,
    imageAspectRatio,
    imageMobileAspectRatio,
    actionLabel,
    cardSize,
    fullWidth,
  }: {
    fromPriceLabel: string;
    noImageLabel: string;
    onSelect?: (id: string) => void;
    imageAspectRatio?: string;
    imageMobileAspectRatio?: string;
    actionLabel?: string;
    cardSize?: 'small' | 'large';
    fullWidth?: boolean;
  }
) {
  const select = onSelect ? () => onSelect(item.id) : undefined;

  const cardProps: Omit<ItemCardProps, 'action' | 'customAction'> = {
    image: { src: item.imageUrl, alt: item.name, placeholderLabel: noImageLabel },
    imageAspectRatio,
    imageMobileAspectRatio: fullWidth ? undefined : imageMobileAspectRatio,
    imageOverlay: true,
    title: item.name,
    meta: item.meta,
    price: item.price
      ? {
          amount: item.price.amount,
          currencyCode: item.price.currencyCode,
          label: fromPriceLabel,
          discount: item.discount,
        }
      : undefined,
    ariaLabel: item.name,
    onSelect: select,
    size: cardSize,
  };

  if (actionLabel && select) {
    return (
      <ItemCard key={item.id} {...cardProps} action={{ label: actionLabel, onClick: select }} />
    );
  }

  return <ItemCard key={item.id} {...cardProps} />;
}

export function Recommendations({
  items,
  onSelect,
  onVisible,
  title,
  titleSecondary,
  titleSize = 'base',
  fromPriceLabel = 'From',
  noImageLabel = 'No image',
  visibleCount = { mobile: 1.2, desktop: 2 },
  layout = 'carousel',
  columns = 2,
  mobileColumns = 2,
  imageAspectRatio,
  imageMobileAspectRatio,
  actionLabel,
  actionAriaLabelFormatter,
  cardSize,
  fillLastRowWhenOdd = false,
  rowGap,
}: RecommendationsProps) {
  const dragToScroll = useDragToScroll<HTMLUListElement>();
  const rowRef = useRef<HTMLElement>(null);
  const isRowInView = useInView(rowRef, { initial: false });
  const onVisibleRef = useRef(onVisible);

  useEffect(() => {
    onVisibleRef.current = onVisible;
  });

  useEffect(() => {
    if (isRowInView) onVisibleRef.current?.();
  }, [isRowInView]);

  if (items.length === 0) return null;

  const cardOptions = {
    fromPriceLabel,
    noImageLabel,
    onSelect,
    imageAspectRatio,
    imageMobileAspectRatio,
    actionLabel,
    cardSize,
  };

  if (layout === 'row') {
    const ariaLabelFor =
      actionAriaLabelFormatter ?? ((name: string) => [actionLabel, name].filter(Boolean).join(' '));

    return (
      <RowSection ref={rowRef}>
        {title && <RowHeading>{title}</RowHeading>}
        <TileRow {...dragToScroll}>
          {items.map((item) => {
            const select = onSelect ? () => onSelect(item.id) : undefined;

            return (
              <Tile key={item.id} onClick={select ? selectOnCardClick(select) : undefined}>
                <TileImage>
                  {item.imageUrl ? (
                    <TileImg
                      src={item.imageUrl}
                      alt=""
                      sizing={TILE_IMAGE_SIZING}
                      draggable={false}
                    />
                  ) : (
                    <TilePlaceholder>{noImageLabel}</TilePlaceholder>
                  )}
                  {select && (
                    <TileAction
                      variant="primary"
                      aria-label={ariaLabelFor(item.name)}
                      onClick={select}
                    >
                      <PlusIcon size={14} />
                    </TileAction>
                  )}
                </TileImage>
                <TileLabel>{item.name}</TileLabel>
              </Tile>
            );
          })}
        </TileRow>
      </RowSection>
    );
  }

  if (layout === 'grid') {
    const fullWidthLast = fillLastRowWhenOdd && items.length % 2 === 1;
    const gridItems = fullWidthLast ? items.slice(0, -1) : items;
    const lastItem = fullWidthLast ? items[items.length - 1] : null;
    const grid = gridItems.length > 0 && (
      <CenteredGrid $columns={columns} $mobileColumns={mobileColumns} $rowGap={rowGap}>
        {gridItems.map((item) => renderItemCard(item, cardOptions))}
      </CenteredGrid>
    );
    return (
      <RecommendationsWrapper>
        {renderHeading(title ?? 'You might also like', titleSecondary, titleSize)}
        {lastItem ? (
          <GridStack $rowGap={rowGap}>
            {grid}
            <FullWidthCell data-full-width="true" $columns={columns}>
              {renderItemCard(lastItem, { ...cardOptions, fullWidth: true })}
            </FullWidthCell>
          </GridStack>
        ) : (
          grid
        )}
      </RecommendationsWrapper>
    );
  }

  const carouselTitle =
    title === undefined ? <RecommendationsTitle>You might also like</RecommendationsTitle> : title;

  return (
    <RecommendationsWrapper>
      <Carousel title={carouselTitle} visibleCount={visibleCount} showProgress>
        {items.map((item) => renderItemCard(item, cardOptions))}
      </Carousel>
    </RecommendationsWrapper>
  );
}
